import {
  type ContentGenerationStatusUpdate,
  type FlowRunResponse,
  type ReferenceValue,
  runFlowTemplate,
  streamFlowTemplateRun,
} from "@/app/_api/flowTemplates";
import type { FlowTemplatePort, FlowTemplateSchema } from "@/app/_api/flowTemplatesServer";
import { HttpError } from "@utilities/api";
import { logger } from "@utilities/logger";
import type { WorkflowResultType } from "../types";

interface RunFlowArgs {
  flowTemplate: FlowTemplateSchema;
  inputs: Record<string, ReferenceValue>;
  resultType: WorkflowResultType;
  signal: AbortSignal;
}

interface RunFlowResult {
  resultUrl: string;
  resultAssetId: string;
  sessionId: string;
}

/**
 * Build the xi `inputs` map for a flow run: each port's uploaded asset becomes a
 * `content_asset_id` reference, then inline texts fill the remaining ports — a
 * port already carrying an asset wins, so its text is skipped.
 */
export function buildFlowInputs({
  assetIdsByPort,
  textsByPort,
}: {
  assetIdsByPort: Record<string, string>;
  textsByPort?: Record<string, string>;
}) {
  const inputs: Record<string, ReferenceValue> = {};
  for (const [portId, assetId] of Object.entries(assetIdsByPort)) {
    inputs[portId] = { content_asset_id: assetId };
  }
  if (textsByPort) {
    for (const [portId, text] of Object.entries(textsByPort)) {
      if (assetIdsByPort[portId]) continue;
      const trimmed = text.trim();
      if (trimmed) inputs[portId] = { inline_text: trimmed };
    }
  }
  return inputs;
}

// Flow templates can be linked to CMS docs whose legacy `workflowSlug` implies
// a different output modality (e.g. a video-producing template under a
// `text-to-image` doc). Fall back to all output ports when none match so we
// don't immediately reject on the first SSE event with "no preferred ports".
function effectivePreferredPorts(outputPorts: FlowTemplatePort[], resultType: WorkflowResultType): FlowTemplatePort[] {
  const matching = outputPorts.filter(p => p.modality === resultType);
  return matching.length > 0 ? matching : outputPorts;
}

function pickCompletedOutput(
  states: Map<string, ContentGenerationStatusUpdate>,
  preferredPorts: FlowTemplatePort[],
): ContentGenerationStatusUpdate | undefined {
  for (const port of preferredPorts) {
    const state = states.get(port.id);
    if (state?.status === "completed" && state.content_url) return state;
  }
  return undefined;
}

function preferredPortsExhausted(
  states: Map<string, ContentGenerationStatusUpdate>,
  preferredPorts: FlowTemplatePort[],
): boolean {
  if (preferredPorts.length === 0) return true;
  return preferredPorts.every(port => {
    const state = states.get(port.id);
    // Ports without a source_node_id can't be routed by status_update events
    // (they're not in nodeIdToPortIds). If the run.outputs seed didn't already
    // produce a usable state, no future event can — treat as terminal so the
    // promise can settle instead of hanging until the EventSource drops.
    if (!port.source_node_id) {
      if (state?.status === "completed" && state.content_url) return false;
      return true;
    }
    if (!state) return false;
    if (state.status === "failed") return true;
    if (state.status === "completed" && !state.content_url) return true;
    return false;
  });
}

/**
 * Map an HTTP status from xi into the `errorType` strings the existing
 * `useCreativeTemplate` error switch understands. Standard xi envelopes
 * (`{ type, code }`) are not parsed by `throwErrorIfResponseNotOk`, so we
 * normalize coarsely by status here instead.
 */
function normalizeHttpErrorType(status: number): string {
  if (status === 429) return "rate_limited";
  if (status === 402) return "quota_exceeded";
  if (status === 400 || status === 422) return "generation_refused";
  if (status >= 500) return "cms_unreachable";
  return "generation_refused";
}

function normalizeError(err: unknown): never {
  if (err instanceof HttpError) {
    const errorType =
      err.errorType === "rate_limited" ||
      err.errorType === "quota_exceeded" ||
      err.errorType === "generation_refused" ||
      err.errorType === "cms_unreachable" ||
      err.errorType === "image_too_large"
        ? err.errorType
        : normalizeHttpErrorType(err.status);
    throw new HttpError(err.status, err.message, errorType, err.data);
  }
  throw err;
}

export async function runFlowAndAwaitResult({
  flowTemplate,
  inputs,
  resultType,
  signal,
}: RunFlowArgs): Promise<RunFlowResult> {
  const run: FlowRunResponse = await runFlowTemplate({
    templateId: flowTemplate.id,
    inputs,
    signal,
  }).catch((err): never => normalizeError(err));

  if (signal.aborted) {
    throw new DOMException("aborted", "AbortError");
  }

  // Ports and resultType are fixed for the run's lifetime, so resolve the
  // preferred set once instead of re-filtering on every SSE event.
  const preferredPorts = effectivePreferredPorts(flowTemplate.output_ports, resultType);

  // node_id → port_id[]: a single source node can feed multiple output ports
  // (fan-out). Storing only one port_id per node would silently drop event
  // routing for siblings.
  const nodeIdToPortIds = new Map<string, string[]>();
  for (const port of flowTemplate.output_ports) {
    if (!port.source_node_id) continue;
    const existing = nodeIdToPortIds.get(port.source_node_id);
    if (existing) existing.push(port.id);
    else nodeIdToPortIds.set(port.source_node_id, [port.id]);
  }

  const portIds = new Set(flowTemplate.output_ports.map(p => p.id));
  const states = new Map<string, ContentGenerationStatusUpdate>();

  // xi's run.outputs map can be keyed by port_id or node_id depending on the
  // run snapshot. Try port_id first, then fall back to the node_id mapping.
  for (const [key, output] of Object.entries(run.outputs)) {
    const seedState: ContentGenerationStatusUpdate = {
      id: output.id,
      generation_session_id: run.id,
      status: output.status,
      content_url: output.content_url,
    };
    if (portIds.has(key)) {
      states.set(key, seedState);
      continue;
    }
    const matchedPortIds = nodeIdToPortIds.get(key);
    if (matchedPortIds && matchedPortIds.length > 0) {
      for (const portId of matchedPortIds) states.set(portId, seedState);
      continue;
    }
    logger.warn(`[runFlowAndAwaitResult] outputs key "${key}" did not match any port or source_node_id`);
  }

  if (run.status === "completed") {
    const chosen = pickCompletedOutput(states, preferredPorts);
    if (chosen?.content_url) {
      return { resultUrl: chosen.content_url, resultAssetId: chosen.id, sessionId: run.id };
    }
    // Opening the SSE here would hang for the full timeout — xi already marked
    // the run done so no further status_update events will arrive.
    logger.warn(
      `[runFlowAndAwaitResult] run ${run.id} reported completed but no port had a pickable ${resultType} output`,
    );
    throw new HttpError(422, "Workflow completed without a usable output", "generation_refused");
  }

  if (run.status === "failed") {
    throw new HttpError(422, "Flow run failed", "generation_refused");
  }

  const eventSource = await streamFlowTemplateRun({
    templateId: flowTemplate.id,
    sessionId: run.id,
  });

  if (signal.aborted) {
    eventSource.close();
    throw new DOMException("aborted", "AbortError");
  }

  return new Promise<RunFlowResult>((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      eventSource.close();
      signal.removeEventListener("abort", onAbort);
    };

    const onAbort = () => {
      cleanup();
      reject(new DOMException("aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort);

    const handleStatusUpdate = (data: string) => {
      let payload: ContentGenerationStatusUpdate;
      try {
        payload = JSON.parse(data) as ContentGenerationStatusUpdate;
      } catch {
        return;
      }
      const matchedPortIds = payload.template_node_id ? nodeIdToPortIds.get(payload.template_node_id) : undefined;
      if (!matchedPortIds || matchedPortIds.length === 0) return;
      for (const portId of matchedPortIds) states.set(portId, payload);

      // Win as soon as any preferred-modality port produces a usable URL — a
      // sibling failure no longer prevents reading a successful result.
      const chosen = pickCompletedOutput(states, preferredPorts);
      if (chosen?.content_url) {
        cleanup();
        resolve({ resultUrl: chosen.content_url, resultAssetId: chosen.id, sessionId: run.id });
        return;
      }

      // Only reject once every preferred-modality port has reached a terminal
      // state without a usable output. Otherwise wait for further events.
      if (preferredPortsExhausted(states, preferredPorts)) {
        const message = payload.error_message ?? "Flow run failed";
        logger.warn(`[runFlowAndAwaitResult] all preferred ports terminal without usable output for run ${run.id}`);
        cleanup();
        reject(new HttpError(422, message, "generation_refused"));
      }
    };

    eventSource.addEventListener("status_update", ev => handleStatusUpdate((ev as MessageEvent<string>).data));
    eventSource.onmessage = ev => handleStatusUpdate(ev.data);

    eventSource.addEventListener("not_found", () => {
      cleanup();
      reject(new HttpError(404, "Flow run not found", "cms_unreachable"));
    });

    eventSource.addEventListener("internal_error", () => {
      cleanup();
      reject(new HttpError(500, "xi internal error", "cms_unreachable"));
    });

    eventSource.onerror = () => {
      // Fail fast: EventSource would otherwise auto-reconnect with the same
      // single-use hCaptcha token embedded in the URL, which xi rejects —
      // so the stream silently hangs in CONNECTING forever. Closing here
      // surfaces the failure to the user.
      cleanup();
      reject(new HttpError(0, "Stream connection lost", "cms_unreachable"));
    };
  });
}
